import React from 'react';
import { Icon } from '@iconify/react';
import { Product, ProductSize } from '../types';
import { CURRENCY } from '../constants';

interface SizeSelectionModalProps {
    product: Product;
    onClose: () => void;
    onSelectSize: (size: ProductSize) => void;
}

const SizeSelectionModal: React.FC<SizeSelectionModalProps> = ({ product, onClose, onSelectSize }) => {
    if (!product.sizes || product.sizes.length === 0) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-scale-in">
                <div className="p-4 border-b border-mauve-light/30 flex justify-between items-center bg-mauve-base/30">
                    <h3 className="font-serif font-bold text-mauve-dark text-lg">Select Size</h3>
                    <button onClick={onClose} className="text-mauve-medium hover:text-mauve-dark transition-colors" aria-label="Close modal">
                        <Icon icon="lucide:x" width="20" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="flex gap-4 mb-6">
                        <div className="w-20 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-mauve-light/20">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                            <h4 className="font-medium text-mauve-text">{product.name}</h4>
                            <p className="text-sm text-mauve-medium">{product.category}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {product.sizes.map((size, index) => (
                            <button
                                key={index}
                                onClick={() => size.stock > 0 && onSelectSize(size)}
                                disabled={size.stock === 0}
                                className={`w-full flex justify-between items-center p-3 rounded-xl border transition-all ${size.stock > 0
                                    ? 'border-mauve-light/50 hover:border-mauve-dark hover:bg-mauve-base/20 cursor-pointer'
                                    : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold ${size.stock > 0 ? 'bg-mauve-dark text-white' : 'bg-gray-300 text-gray-500'
                                        }`}>
                                        {size.name}
                                    </span>
                                    <span className={size.stock > 0 ? 'text-mauve-text font-medium' : 'text-gray-400'}>
                                        {size.stock > 0 ? `${CURRENCY}${size.price}` : 'Sold Out'}
                                    </span>
                                </div>
                                {size.stock > 0 && (
                                    <span className="text-xs text-mauve-medium">
                                        {size.stock} left
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SizeSelectionModal;
